import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { ShopperAiWrappedPageSchema } from "@redotech/redo-merchant-support-rpc/schema/get-shopper-ai-wrapped";
import {
  SHOPPER_AI_WRAPPED_IMAGE_URLS,
  ShopperAiWrappedImageKey,
} from "@redotech/redo-model/shopper-ai-wrapped";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Header, Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { OnboardingProps } from "../common";

function getImageUrl(imageKey: ShopperAiWrappedImageKey): string {
  return SHOPPER_AI_WRAPPED_IMAGE_URLS[imageKey];
}

const PageContent = memo(function PageContent({
  page,
}: {
  page: ShopperAiWrappedPageSchema;
}) {
  const hasMetricValue = page.metricValue && page.metricValue !== "";

  return (
    <Flex align="center" dir="column" gap="xl" w="full">
      <Flex height="fit-content" position="relative" w="full">
        <img
          alt={`Shopper AI Wrapped - ${page.type}`}
          src={getImageUrl(page.imageKey)}
          style={{
            width: "100%",
            height: "200px",
            borderRadius: "8px",
            objectFit: "cover",
          }}
        />
        {(page.metricValue || page.metricLabel) && (
          <Flex
            align="center"
            bottom="0"
            dir="column"
            justify="center"
            left="0"
            position="absolute"
            px="xl"
            right="0"
            top="0"
          >
            {hasMetricValue ? (
              <>
                <Header fontSize="5xl" fontWeight="bold" textColor="white">
                  {page.metricValue}
                </Header>
                <Text fontSize="sm" mt="xs" textColor="white">
                  {page.metricLabel}
                </Text>
              </>
            ) : (
              <Header
                fontSize="3xl"
                fontWeight="bold"
                textAlign="center"
                textColor="white"
              >
                {page.metricLabel}
              </Header>
            )}
          </Flex>
        )}
      </Flex>
      <Text
        fontSize="md"
        overflow="hidden"
        textAlign="left"
        textColor="tertiary"
        w="full"
      >
        {page.bodyText}
      </Text>
    </Flex>
  );
});

export const ShopperAiWrappedModal = memo(function ShopperAiWrappedModal({
  handleDismiss,
  handleComplete,
}: OnboardingProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);

  const wrappedData = useLoad(
    async () => supportRpcClient.getShopperAiWrapped(),
    [supportRpcClient],
  );

  if (wrappedData.pending || !wrappedData.value) {
    return null;
  }

  const { pages } = wrappedData.value;
  const totalPages = pages.length;
  const currentPageData = pages[currentPage];

  if (!currentPageData) {
    return null;
  }

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getPrimaryButtonText = () => {
    if (currentPageData.type === "intro") {
      return "Let's go";
    }
    if (currentPageData.type === "closing") {
      return "Done";
    }
    return "Next";
  };

  const getSecondaryButton = () => {
    if (currentPageData.type !== "intro") {
      return { text: "Back", onClick: handleBack, size: "md" as const };
    }
    return undefined;
  };

  const getAdditionalFooterContent = () => {
    if (
      currentPageData.type === "recommendation" &&
      currentPageData.recommendation?.actionUrl
    ) {
      return (
        <RedoButton
          hierarchy="secondary"
          onClick={() =>
            window.open(currentPageData.recommendation?.actionUrl, "_blank")
          }
          size="md"
          text="Enable"
        />
      );
    }
    return undefined;
  };

  return (
    <RedoModal
      additionalFooterContent={getAdditionalFooterContent()}
      isOpen
      modalSize="sm"
      onModalCloseRequested={handleDismiss}
      primaryButton={{
        text: getPrimaryButtonText(),
        onClick: handleNext,
        size: "md",
      }}
      secondaryButton={getSecondaryButton()}
      title={currentPageData.title}
    >
      <PageContent page={currentPageData} />
    </RedoModal>
  );
});
